import type { RouteLocationNamedRaw } from 'vue-router';
import { VaultFileRevisionKind, type ContractManagementContractFileInDb } from '@/api';

// Route builders for pages linked from more than one feature. One place owns
// each route's name and param shape, so a `<router-link :to>` and a
// `router.push()` to the same page can't drift apart. Pages linked from a
// single file keep their inline route object. Extra query goes on at the call
// site: `{ ...contractRoute(uid), query }`.

export const contractRoute = (uid: string): RouteLocationNamedRaw => ({
  name: 'contract-management-contract-detail',
  params: { uid },
});

export const contractFileRoute = (uid: string): RouteLocationNamedRaw => ({
  name: 'contract-management-contract-file-detail',
  params: { uid },
});

/** A draft, back in the wizard; the uid is its vault file's. */
export const draftRoute = (vaultFileUid: string): RouteLocationNamedRaw => ({
  name: 'contract-generation-draft',
  params: { draftUid: vaultFileUid },
});

/** Where a contract file opens: its page — or, while it is still a draft, the wizard. */
export const contractFileOpenRoute = (
  file: ContractManagementContractFileInDb
): RouteLocationNamedRaw =>
  file.vault_file?.current_revision?.kind === VaultFileRevisionKind.TEMPLATED
    ? draftRoute(file.vault_file.uid)
    : contractFileRoute(file.uid);

export const vaultDirectoryRoute = (directoryUid: string): RouteLocationNamedRaw => ({
  name: 'file-vault-directory',
  params: { directoryUid },
});

export const contractualPartyRoute = (uid: string): RouteLocationNamedRaw => ({
  name: 'contract-management-detail',
  params: { entityType: 'contractual-party', uid },
});

export const templateRoute = (uid: string): RouteLocationNamedRaw => ({
  name: 'contract-generation-template-detail',
  params: { uid },
});

export const templateVariantRoute = (uid: string, variantUid: string): RouteLocationNamedRaw => ({
  name: 'contract-generation-template-variant-detail',
  params: { uid, variantUid },
});

export const signingRequestRoute = (signingRequestUid: string): RouteLocationNamedRaw => ({
  name: 'signing-request-detail',
  params: { signingRequestUid },
});

export const signingRequestEditRoute = (signingRequestUid: string): RouteLocationNamedRaw => ({
  name: 'signing-request-edit',
  params: { signingRequestUid },
});

export const reviewProjectRoute = (uid: string): RouteLocationNamedRaw => ({
  name: 'review-project-detail',
  params: { uid },
});

export const playbookRunsRoute = (uid: string): RouteLocationNamedRaw => ({
  name: 'playbook-runs',
  params: { uid },
});

export const playbookEditRoute = (uid: string): RouteLocationNamedRaw => ({
  name: 'playbook-edit',
  params: { uid },
});

export const permissionRoleRoute = (uid: string): RouteLocationNamedRaw => ({
  name: 'permission-role-detail',
  params: { uid },
});

export const contractTypeRoute = (uid: string): RouteLocationNamedRaw => ({
  name: 'contract-type-detail',
  params: { uid },
});
